<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setMode']);

// Horizontal padding (px-2 => 8px each side) around each segment's content;
// the measured content width is offset by this to size the sliding chip.
const SEGMENT_PADDING = 16;

const MODES = [
  REPLY_EDITOR_MODES.REPLY,
  REPLY_EDITOR_MODES.NOTE,
  REPLY_EDITOR_MODES.FOLLOW_UP,
];

const replyRef = useTemplateRef('replyRef');
const noteRef = useTemplateRef('noteRef');
const followUpRef = useTemplateRef('followUpRef');

const { width: replyWidth } = useElementSize(replyRef);
const { width: noteWidth } = useElementSize(noteRef);
const { width: followUpWidth } = useElementSize(followUpRef);

// When replies are restricted, reply mode is not selectable, so fall back to
// the private note position for the sliding chip.
const activeMode = computed(() => {
  if (props.isReplyRestricted && props.mode === REPLY_EDITOR_MODES.REPLY) {
    return REPLY_EDITOR_MODES.NOTE;
  }
  return props.mode;
});

const isFollowUpActive = computed(
  () => activeMode.value === REPLY_EDITOR_MODES.FOLLOW_UP
);

const segmentWidths = computed(() => ({
  [REPLY_EDITOR_MODES.REPLY]: replyWidth.value + SEGMENT_PADDING,
  [REPLY_EDITOR_MODES.NOTE]: noteWidth.value + SEGMENT_PADDING,
  [REPLY_EDITOR_MODES.FOLLOW_UP]: followUpWidth.value + SEGMENT_PADDING,
}));

const chipWidth = computed(() => `${segmentWidths.value[activeMode.value]}px`);

const chipTranslate = computed(() => {
  const index = MODES.indexOf(activeMode.value);
  const offset = MODES.slice(0, index).reduce(
    (sum, mode) => sum + segmentWidths.value[mode],
    0
  );
  return `${offset}px`;
});

const selectMode = mode => {
  if (props.disabled) return;
  if (mode === REPLY_EDITOR_MODES.REPLY && props.isReplyRestricted) return;
  emit('setMode', mode);
};
</script>

<template>
  <div
    class="flex items-center w-auto h-8 p-1 transition-all border border-n-weak rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    :class="{ 'cursor-not-allowed': disabled }"
  >
    <button
      ref="replyRef"
      type="button"
      :disabled="disabled || isReplyRestricted"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer disabled:cursor-not-allowed"
      @click="selectMode(REPLY_EDITOR_MODES.REPLY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      ref="noteRef"
      type="button"
      :disabled="disabled"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer disabled:cursor-not-allowed"
      @click="selectMode(REPLY_EDITOR_MODES.NOTE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <button
      ref="followUpRef"
      type="button"
      :disabled="disabled"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer disabled:cursor-not-allowed"
      :class="{ 'text-n-teal-11': isFollowUpActive }"
      @click="selectMode(REPLY_EDITOR_MODES.FOLLOW_UP)"
    >
      {{ $t('CONVERSATION.FOLLOW_UP.MODE_LABEL') }}
    </button>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)]"
      :class="[
        isFollowUpActive ? 'bg-n-teal-3' : 'bg-n-solid-1',
        { 'transition-all duration-300': !disabled },
      ]"
      :style="{
        '--chip-width': chipWidth,
        '--translate-x': chipTranslate,
        '--rtl-translate-x': `calc(-1 * ${chipTranslate})`,
      }"
    />
  </div>
</template>
